import { Subject, take, takeUntil } from 'rxjs';

import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IJobCardForm } from '@job-card/models/job-card';
import { JobCardStorageService } from '@job-card/services/job-card-storage/job-card-storage.service';
import { AlertComponent } from '@shared/components/alerts/alert/alert.component';
import { FileUploadComponent } from '@shared/components/forms/file-upload/file-upload.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import { AlertTypes } from '@shared/models/alert';
import { FileTypeService } from '@shared/models/file-types';
import { IStorageFile } from '@shared/models/storage';
import { ToastTypes } from '@shared/models/toast';
import { ModalService } from '@shared/services/modal/modal.service';
import { ToastService } from '@shared/services/toast/toast.service';

import { AttachmentDescriptionModalComponent } from '../attachment-description-modal/attachment-description-modal.component';
import { JobCardAttachmentComponent } from '../job-card-attachment/job-card-attachment.component';

@Component({
	selector: 'app-job-card-attachments',
	imports: [
		CommonModule,
		FormsModule,
		LoadingStateComponent,
		ReactiveFormsModule,
		FileUploadComponent,
		AlertComponent,
		JobCardAttachmentComponent,
	],
	templateUrl: './job-card-attachments.component.html',
	styleUrl: './job-card-attachments.component.scss',
})
export class JobCardAttachmentsComponent implements OnInit, OnDestroy {
	@Input() form: FormGroup<IJobCardForm>;
	@Input() organisationId: string;
	@Input() leadRequestId: string;
	@Input() scheduledAssessmentId: string;
	@Input() mustRefreshAttachments: Subject<void>;
	@Input() disabled: boolean = false;

	destroyed$ = new EventEmitter<void>();
	private jobCardStorageService = inject(JobCardStorageService);
	private toastService = inject(ToastService);
	private modalService = inject(ModalService);

	AlertTypes = AlertTypes;
	allowedTypes: Array<string> = FileTypeService.getMediaAndDocTypes();

	attachments: Array<IStorageFile> = [];
	isLoading = true;

	ngOnInit(): void {
		this.mustRefreshAttachments
			.asObservable()
			.pipe(takeUntil(this.destroyed$))
			.subscribe(() => this.fetchAttachments());
		this.fetchAttachments();
	}

	ngOnDestroy(): void {
		this.destroyed$.emit();
		this.mustRefreshAttachments.complete();
	}

	fetchAttachments(): void {
		this.isLoading = true;
		this.jobCardStorageService
			.getJobCardAttachments(this.organisationId, this.leadRequestId, this.scheduledAssessmentId)
			.then((attachments) => {
				this.attachments = attachments;
			})
			.catch(() => {
				this.toastService.showToast({
					title: 'Error',
					message: 'Failed to load attachments. Please try again later.',
					type: ToastTypes.ERROR,
				});
			})
			.finally(() => {
				this.isLoading = false;
			});
	}

	removeAttachment(attachment: IStorageFile): void {
		const modalRef = this.modalService.open(ConfirmationModalComponent, false, 'md');
		const componentInstance = modalRef.componentInstance as ConfirmationModalComponent;
		componentInstance.title = 'Remove Attachment';
		componentInstance.contentText = `Are you sure you want to remove the attachment "${attachment.name}"?`;
		componentInstance.confirmAction.subscribe(() => {
			this.jobCardStorageService
				.deleteJobCardAttachment(attachment.fullPath)
				.then(() => {
					this.mustRefreshAttachments.next();
					this.toastService.showToast({
						title: 'Success',
						message: 'Attachment removed successfully.',
						type: ToastTypes.SUCCESS,
					});
				})
				.catch(() => {
					this.toastService.showToast({
						title: 'Error',
						message: 'Failed to remove attachment. Please try again later.',
						type: ToastTypes.ERROR,
					});
				});
		});
	}

	editAttachmentDescription(attachment: IStorageFile): void {
		const modalRef = this.modalService.open(AttachmentDescriptionModalComponent, false, 'md');
		const componentInstance = modalRef.componentInstance as AttachmentDescriptionModalComponent;
		componentInstance.description = attachment.customMetadata!.description;
		componentInstance.descriptionSaved.pipe(take(1)).subscribe((newDescription: string) => {
			this.jobCardStorageService
				.editJobCardMetaData(attachment.fullPath, {
					description: newDescription,
				})
				.then(() => {
					this.mustRefreshAttachments.next();
					this.toastService.showToast({
						title: 'Success',
						message: 'Attachment description updated',
						type: ToastTypes.SUCCESS,
					});
				})
				.catch(() => {
					this.toastService.showToast({
						title: 'Error',
						message: 'Failed to edit attachment. Please try again later.',
						type: ToastTypes.ERROR,
					});
				});
		});
	}
}
